import {
  FIELD_TYPE_CATALOGUE,
  type FieldFormat,
  type FieldType,
  type ValueTone,
} from '@crm/contracts';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { FieldCell } from '../fields/cell.tsx';
import { FieldEditor } from '../fields/editor.tsx';

/**
 * Inline cell editing (RELAY-942): double-click (or Enter / F2 on the focused cell) swaps the
 * value for the type's editor from the catalogue; Enter or leaving the cell commits, Escape
 * cancels. The cell is dumb about persistence — `onCommit` is the page's PATCH — so the same
 * cell edits a standard column and a custom field alike.
 */
export function EditableCell({
  type,
  value,
  options,
  format,
  tone,
  label,
  canEdit,
  onCommit,
}: {
  type: FieldType;
  value: unknown;
  options?: ReadonlyArray<{ value: string; label: string }> | undefined;
  /** The custom field's display format, for the read-only value. */
  format?: FieldFormat | null | undefined;
  /** The read-only badge's colour (a stage's outcome). */
  tone?: ValueTone | undefined;
  label: string;
  canEdit: boolean;
  onCommit: (next: unknown) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<unknown>(value);
  const committed = useRef(false);
  const editorRef = useRef<HTMLDivElement>(null);
  // The editor takes focus when it opens, so a double-click is immediately followed by typing.
  useEffect(() => {
    if (editing)
      editorRef.current?.querySelector<HTMLElement>('input, textarea, button, [tabindex]')?.focus();
  }, [editing]);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const start = () => {
    if (!canEdit) return;
    committed.current = false;
    setDraft(value);
    setEditing(true);
  };
  const commit = (next: unknown = draft) => {
    if (committed.current) return;
    committed.current = true;
    setEditing(false);
    if (JSON.stringify(next) !== JSON.stringify(value)) void onCommit(next);
  };
  const cancel = () => {
    committed.current = true;
    setEditing(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      cancel();
    } else if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      e.preventDefault();
      commit();
    }
  };

  // A select-typed field with no option list (status, work type: open vocabularies on the
  // standard columns) is edited as free text; the catalogue's select needs options.
  const editorType: FieldType =
    FIELD_TYPE_CATALOGUE[type].editor === 'select' && !options?.length ? 'text' : type;
  // Picking a select's option is the edit: it commits at once, as a spreadsheet dropdown does.
  const picks = FIELD_TYPE_CATALOGUE[editorType].editor === 'select';
  if (editing)
    return (
      <div
        ref={editorRef}
        className="w-full"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
        onBlur={(e) => {
          // Leaving the whole editor (not moving between its parts, e.g. currency + unit) commits.
          // A select's option list opens in a portal, outside the editor: focus there stays in it.
          const to = e.relatedTarget;
          if (!e.currentTarget.contains(to) && !to?.closest('[role="listbox"]')) commit();
        }}
        data-testid="cell-editor"
      >
        <FieldEditor
          type={editorType}
          value={draft}
          onChange={(next) => {
            setDraft(next);
            if (picks) commit(next);
          }}
          options={options}
          label={label}
        />
      </div>
    );
  return (
    <div
      className={canEdit ? 'w-full cursor-text truncate' : 'w-full truncate'}
      tabIndex={canEdit ? 0 : undefined}
      onDoubleClick={(e) => {
        if (!canEdit) return;
        e.stopPropagation();
        start();
      }}
      onKeyDown={(e) => {
        if (canEdit && (e.key === 'Enter' || e.key === 'F2')) {
          e.preventDefault();
          start();
        }
      }}
      data-testid="cell-value"
      aria-label={canEdit ? `${label} (double-click to edit)` : undefined}
    >
      <FieldCell type={type} value={value} format={format} tone={tone} />
    </div>
  );
}
